import type { ReactNode } from 'react';

interface MountainBackgroundProps {
  children: ReactNode;
  variant?: 'prominent' | 'subtle';
  className?: string;
}

/**
 * Mountain Background Component
 *
 * Displays the brand's mountain SVG as a fixed background image.
 * - 'prominent': Login/auth pages (50-60% opacity)
 * - 'subtle': Interior/authenticated pages (10-20% opacity)
 *
 * CURRENT IMPLEMENTATION:
 * - Uses 100% width scaling with strategic cropping
 * - Base and mid-section always visible, tops may crop on wide screens (>1200px)
 *
 * TODO (Future Enhancement):
 * - Create wider SVG (3:2 or 16:9 aspect ratio) for perfect coverage without cropping
 * - Current SVG is ~1:1 ratio (2001×2014px), ideal would be ~3000×1500px
 * - See: docs/design-system/mountain-background-enhancement.md
 */
export function MountainBackground({
  children,
  variant = 'subtle',
  className = ''
}: MountainBackgroundProps) {
  const opacityClass = variant === 'prominent'
    ? 'opacity-[0.55]'
    : 'opacity-[0.15] dark:opacity-[0.10]';

  return (
    <div className={`relative min-h-screen ${className}`}>
      {/* Fixed mountain background - full width with strategic cropping */}
      <div
        className="fixed bottom-0 left-0 right-0 h-[75vh] overflow-hidden pointer-events-none z-0"
        aria-hidden="true"
      >
        <div
          className={`absolute inset-0 bg-no-repeat transition-opacity duration-500 ${opacityClass}`}
          style={{
            backgroundImage: 'url(/assets/mountains.svg?v=2)',
            backgroundPosition: 'center bottom',
            backgroundSize: '100% auto',
          }}
        />
      </div>

      {/* Content layer */}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}
